import {
  batchCreateEvent,
  batchDeleteElementAndSubs,
  getNextUuid,
  getNestedSubelementsByType,
  createStandardizedFieldValue,
  createFieldValue,
  batchEditEventType,
  batchCreateEventType,
  batchCreateBasicField,
  getSlsTypeByValue,
  batchCreateIntermediarySuperForField,
  INDEXED_INCORRECTLY,
  batchCreateRelationship,
  getNestedOrFloatingElementById,
  getRelationshipsBetweenTwoPeopleByType,
} from '@fs/zion-sls'
import { replaceRelationshipPerson } from './relationship.utils'
import { getFieldValueText } from '../../panels/highlightPanel/utils/highlightUtils'
import { hasAnyDirtyField } from '../../../../utils/formResolvers.utils'
import { compareLinkedPersons } from '../../panels/linkNamesPanel/utils/linkedPersonsHelpers'

const ERR_PERSON_ID = 'Primary person must have an ID'

/**
 * Returns an attribution object for event changes.
 * @param {string} appName - The name of the application making the change.
 * @returns {{comment: string, reasonId: string, app: string}} Attribution object.
 */
function getAttribution(appName) {
  return { comment: '', reasonId: INDEXED_INCORRECTLY, app: appName }
}

/**
 * Validates the inputs for dispatching event changes.
 * @param {Object} person - The person object, must have an id.
 * @param {Array} events - The array of event objects.
 * @throws {Error} If person or person.id is missing.
 * @returns {boolean} True if inputs are valid, false otherwise.
 */
function validateDispatchInputs(person, events) {
  if (!person?.id) throw new Error(ERR_PERSON_ID)
  return Array.isArray(events)
}

/**
 * Prepares a field value object for dispatch.
 * @param {Object} fieldObj - The field object containing value and type.
 * @param {string} appName - The name of the application.
 * @returns {Object} The prepared field value.
 */
function prepareFieldValue(fieldObj, appName) {
  let fieldValue = {}

  if (fieldObj?.value?.standard) {
    fieldValue = createStandardizedFieldValue(fieldObj.value, fieldObj.type, getAttribution(appName))
  } else {
    const newValue = getFieldValueText(fieldObj)
    fieldValue = createFieldValue(newValue, getAttribution(appName))
  }

  return fieldValue
}

/**
 * Gets the field value for an event type.
 * @param {Object} event - The event object.
 * @param {Array} slsTypes - The list of SLS types.
 * @param {string} appName - The name of the application.
 * @returns {Object} The event type field value.
 */
function getEventTypeFieldValue(event, slsTypes, appName) {
  const eventType = getSlsTypeByValue(event.eventType?.key, 'events', slsTypes)
  const eventTypeLabel = eventType?.displayLabel
  return createFieldValue(eventTypeLabel, getAttribution(appName), eventType?.uri)
}

/**
 * Finds the element ID from a list of dispatches for a given element type.
 * @param {Array} dispatches - The list of dispatch actions.
 * @param {string} elementType - The element type to search for.
 * @returns {string|undefined} The found element ID, or undefined.
 */
function findElementIdFromCreate(dispatches, elementType) {
  return dispatches?.find((action) => action.type === 'CREATE_ELEMENT' && action.value.elementType === elementType)
    ?.value.id
}

export function handleExistingEventField(fieldObj, event, defaultValues, person, appName) {
  if (!fieldObj) return []
  const dispatches = []
  const originalFieldValue = getFieldValueText(defaultValues)
  const newFieldValueCandidate = getFieldValueText(fieldObj)
  const existingFieldDispatches = fieldObj.dispatches || []
  const existingFieldValueDispatch = existingFieldDispatches.find(
    (d) => d.type === 'ADD_FV' && d.value.id === fieldObj.id
  )

  const validateStandardValue = (originalStandardValue, currentStandardValue, type) => {
    if (type === 'DATE') {
      return originalStandardValue?.formalText === currentStandardValue?.formalText
    }

    if (type === 'PLACE') {
      return originalStandardValue?.id === currentStandardValue?.id
    }

    return originalStandardValue == null && currentStandardValue == null
  }

  if (
    originalFieldValue === newFieldValueCandidate &&
    validateStandardValue(defaultValues?.value?.standard, fieldObj.value?.standard, fieldObj.type)
  ) {
    return []
  }

  const fieldValue = prepareFieldValue(fieldObj, appName)

  if (!fieldObj.elementId) {
    // This field wasn't read from sls, so we need to create it
    const existingIntermediary = fieldObj?.elementId
    const intermediaryId = existingIntermediary || getNextUuid()
    if (!existingIntermediary) {
      const newFieldTypeDispatches = batchCreateIntermediarySuperForField({
        partitionKey: person?.partitionKey,
        intermediaryType: fieldObj?.type,
        intermediaryId,
        superId: event.id,
        superType: 'EVENT',
      })

      dispatches.push(...newFieldTypeDispatches)
    }

    const newField = batchCreateBasicField({
      partitionKey: person?.partitionKey,
      superId: intermediaryId,
      fieldType: fieldObj?.type,
      fieldValue,
      id: fieldObj?.id,
      superElementType: fieldObj?.type,
    })
    dispatches.push(...newField)
  } else if (fieldValue) {
    // Use existing dispatches for this field (e.g. from the shared highlight flow)
    if (existingFieldValueDispatch) {
      dispatches.push(...existingFieldDispatches)
    } else {
      // If no existing dispatch, create an ADD_FV dispatch to update the field value
      dispatches.push({
        type: 'ADD_FV',
        value: { partitionKey: person?.partitionKey, id: fieldObj.id, fieldValue },
      })
    }
  }
  return dispatches
}

/**
 * Handles updates to existing events and returns dispatch actions.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.defaultValues - The default values for the event.
 * @param {Object} params.person - The person object.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for existing events.
 */
export function handleExistingEvents({ event, defaultValues, person, slsTypes, dirtyFields, appName }) {
  const dispatches = []
  if (!event || !person.partitionKey || !event?.id) return dispatches
  // Only process updates if any field in this event was modified
  if (hasAnyDirtyField(dirtyFields)) {
    Object.keys(dirtyFields).forEach((field) => {
      if (field === 'eventType') {
        const existingEventTypeFieldId = event?.existingEventTypeFieldId
        const hasEventTypeField = !!existingEventTypeFieldId
        const originalEventType = defaultValues?.eventType?.key
        const newEventType = event.eventType?.key

        const newEventTypeFieldValue = getEventTypeFieldValue(event, slsTypes, appName)

        if (hasEventTypeField && originalEventType !== newEventType) {
          dispatches.push(
            ...batchEditEventType({
              partitionKey: defaultValues?.partitionKey,
              typeFieldValue: newEventTypeFieldValue,
              typeFieldId: existingEventTypeFieldId,
            })
          )
        } else if (!hasEventTypeField) {
          dispatches.push(
            ...batchCreateEventType({
              partitionKey: defaultValues?.partitionKey,
              typeFieldValue: newEventTypeFieldValue,
              typeFieldId: getNextUuid(),
              elementId: event.id,
            })
          )
        }
      } else if (field === 'relationship') {
        dispatches.push(
          ...replaceRelationshipPerson({
            relationship: event?.relationship?.data,
            personId: person.id,
            otherPersonId: event?.relationship?.otherPerson?.key,
          })
        )
      } else if (['dates', 'places', 'other'].includes(field) && event[field]?.length > 0) {
        event[field]?.forEach?.((fieldObj, index) => {
          const newEexistingFieldDispatches = handleExistingEventField(
            fieldObj,
            event,
            defaultValues?.[field]?.[index],
            person,
            appName
          )
          dispatches.push(...newEexistingFieldDispatches)
        })
      }
    })
  }

  return dispatches
}

/**
 * Attach existing event to current person or to all primary persons if the event is primary.
 * Then submit field updates.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object from SLS.
 * @param {Object} params.person - The person object from SLS.
 * @param {Array} params.images - The images from SLS.
 * @param {Array} params.defaultValues - The list of default values for fields.
 * @param {Object} params.record - The record object from SLS.
 * @param {Array} params.primaryPersons - The list of images from SLS state.
 * @param {Object} params.dirtyFields - The dirty fields object from formState for this event.
 * @param {string} params.appName - The application name.
 * @returns {Array} dispatches
 */
function attachExistingEvent({ event, person, record, primaryPersons, dirtyFields, appName, defaultValues }) {
  const dispatches = []
  if (event.primary && record) {
    // Make sure this event is linked to all primary persons in this record, including current person if they are primary.
    primaryPersons?.forEach((primaryPerson) => {
      if (!event.linkedPersonIds?.some((supEl) => supEl.id === primaryPerson.id)) {
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: primaryPerson.partitionKey,
            subPartitionKey: primaryPerson.partitionKey, // Events are in same partition as person
            superId: primaryPerson.id,
            subId: event.existingEventId,
            elementType: 'PERSON',
          },
        })
      }
    })
  } else {
    // Link the person to the existing event
    dispatches.push({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: person.partitionKey,
        subPartitionKey: person.partitionKey, // Events are in same partition as person
        superId: person.id,
        subId: event.existingEventId,
        elementType: 'PERSON',
      },
    })
  }

  // Update field values for the existing event.
  if (hasAnyDirtyField(dirtyFields?.dates)) {
    event.dates?.forEach?.((dateFieldObj, index) => {
      dispatches.push(...handleExistingEventField(dateFieldObj, event, defaultValues?.dates?.[index], person, appName))
    })
  }
  if (hasAnyDirtyField(dirtyFields?.places)) {
    event.places?.forEach?.((placeFieldObj, index) => {
      dispatches.push(
        ...handleExistingEventField(placeFieldObj, event, defaultValues?.places?.[index], person, appName)
      )
    })
  }
  return dispatches
}

/**
 * Creates event and adds it to the person or primaryPersons. If this is a relationship event, this adds the relationship.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.person - The person object.
 * @param {Boolean} params.attachToThisPerson - Whether to attach the event to this person or to all primary persons.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Boolean} params.createDate - Whether to create a DATE element
 * @param {Boolean} params.createPlace - Whether to create a PLACE element
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for new events.
 */
function handleEventAndRelationshipCreation({
  person,
  event,
  attachToThisPerson,
  primaryPersons,
  slsTypes,
  createDate,
  createPlace,
  appName,
}) {
  const newEventId = event.id || getNextUuid()
  const eventType = getSlsTypeByValue(event?.eventType?.key || event?.eventType, 'events', slsTypes)
  const eventTypeFieldValue = getEventTypeFieldValue(event, slsTypes, appName)
  let eventParentId = attachToThisPerson ? person.id : null
  const otherPersonId = event?.relationship?.otherPerson?.key
  const relType = event?.eventType?.relType
  const dispatches = []

  // Creating relationship if applicable
  const isRelationshipEvent = event?.eventType?.isRelationshipEvent
  if (isRelationshipEvent) {
    // Check for existing relationship of the correct type that can be reused to attach the event to
    const possibleExistingRelationship = getRelationshipsBetweenTwoPeopleByType({
      person,
      otherPersonId,
      relType,
    })?.[0]
    const existingEvent = getNestedSubelementsByType(possibleExistingRelationship, 'EVENT')?.[0]

    // If no event found, reuse the relationship, else create a new relationship
    if (possibleExistingRelationship && !existingEvent) {
      eventParentId = possibleExistingRelationship?.id
    } else {
      eventParentId = getNextUuid()
      const relationshipDispatches = batchCreateRelationship({
        id: eventParentId,
        partitionKey: person.partitionKey,
        person1ID: attachToThisPerson ? person?.id : primaryPersons[0]?.id,
        person2ID: otherPersonId,
        relType,
      })

      dispatches.push(...relationshipDispatches)
    }
  }
  dispatches.push(
    ...batchCreateEvent({
      partitionKey: person.partitionKey,
      eventType: eventType?.eventType,
      personId: eventParentId,
      id: newEventId,
      eventTypeFieldValue,
      createDate,
      createPlace,
      eventTypeId: getNextUuid(),
      extraArgs: { primary: event.primary || false },
      personIdAsRelationshipId: isRelationshipEvent,
    })
  )

  if (event.primary && !isRelationshipEvent) {
    primaryPersons?.forEach((primaryPerson) => {
      // Make all primary persons share this event
      if (primaryPerson?.id !== person.id) {
        // Ignore current person since we added them above unless they are not primary
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: primaryPerson.partitionKey,
            subPartitionKey: primaryPerson.partitionKey,
            superId: primaryPerson.id,
            subId: newEventId,
            elementType: 'PERSON',
          },
        })
      }
    })
  }
  return dispatches
}

/**
 * Handles creation of new events and returns dispatch actions.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Object} params.person - The person object.
 * @param {Array} params.images - The list of images from SLS state.
 * @param {Array} params.defaultValues - The list of default values for fields.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object from formState for this event.
 * @param {string} params.appName - The application name.
 * @returns {Array} List of dispatch actions for new events.
 */
export function handleNewEvents({ event, person, images, slsTypes, dirtyFields, appName, defaultValues }) {
  const dispatches = []
  if (!event || !person?.id || !person?.partitionKey || !event?.eventType) {
    return dispatches
  }
  const attachToThisPerson = !event.primary || person.primary // False if event is primary and person is not, meaning we modify primary persons instead
  const record = person.recordId ? getNestedOrFloatingElementById(images, person.recordId) : null
  const primaryPersons = record?.subElements?.filter((se) => se.elementType === 'PERSON' && se.primary)

  if (!attachToThisPerson && !primaryPersons?.length) {
    return dispatches
  }
  // Check if this is linking to an existing event
  if (event.existingEventId) {
    return dispatches.concat(
      attachExistingEvent({ event, person, defaultValues, record, primaryPersons, dirtyFields, appName })
    )
  }

  // Determine if we need to create date/place fields based on presence of data
  const createDate = !!getFieldValueText(event?.dates?.[0])
  const createPlace = !!getFieldValueText(event?.places?.[0])

  dispatches.push(
    ...handleEventAndRelationshipCreation({
      person,
      event,
      attachToThisPerson,
      primaryPersons,
      slsTypes,
      createDate,
      createPlace,
      appName,
    })
  )

  const dateId = findElementIdFromCreate(dispatches, 'DATE')
  const placeId = findElementIdFromCreate(dispatches, 'PLACE')

  Object.keys(event).forEach((field) => {
    if (['dates', 'places'].includes(field) && event[field]?.length > 0) {
      const fieldObj = event[field]?.[0]
      const existingDispatches = fieldObj?.dispatches || []
      if (!fieldObj) return
      const fieldValue = prepareFieldValue(fieldObj, appName)
      let fieldId = null
      if (fieldObj.type === 'DATE' && createDate) {
        fieldId = dateId
      } else if (fieldObj.type === 'PLACE' && createPlace) {
        fieldId = placeId
      }
      if (fieldId) {
        const existingFieldValueDispatch = existingDispatches.find(
          (d) => d.type === 'ADD_FV' && d.value.id === fieldObj.id
        )

        // If the field value was previously created (e.g by the shared highlight flow), reuse the dispatches for this field
        if (existingFieldValueDispatch) {
          dispatches.push(...fieldObj.dispatches)
          dispatches.push({
            type: 'ADD_SUB',
            value: {
              superPartitionKey: person.partitionKey,
              subPartitionKey: person.partitionKey,
              superId: fieldId,
              subId: fieldObj.id,
              elementType: fieldObj.type,
            },
          })
        } else {
          dispatches.push(
            ...batchCreateBasicField({
              partitionKey: person.partitionKey,
              superId: fieldId,
              fieldType: fieldObj.type,
              fieldValue,
              superElementType: fieldObj.type,
              id: fieldObj.id,
            })
          )
        }
      }
    }
  })

  return dispatches
}

/**
 * Handles deletion of an event and returns dispatch actions.
 * @param {Object} event - The event object to delete.
 * @returns {Array} List of dispatch actions for deleting the event.
 */
export function handleDeletedEvents(event) {
  const dispatches = []

  dispatches.push(
    ...batchDeleteElementAndSubs({
      element: event,
    })
  )
  return dispatches
}

/**
 * Gets dispatch actions for a single event, handling both new and existing events.
 * @param {Object} params - The parameters object.
 * @param {Object} params.event - The event object.
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.defaultValues - The default values for the event.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {number} params.eventIndex - The index of the event.
 * @returns {Array} List of dispatch actions for the event.
 */
function getEventDispatchesForSingleEvent({
  event,
  person,
  images,
  appName,
  slsTypes,
  dirtyFields,
  defaultValues,
  eventIndex,
}) {
  if (!event) return []

  if (!event.added) {
    return handleExistingEvents({
      event,
      defaultValues: defaultValues?.find((dv) => dv.id === event.id),
      person,
      appName,
      slsTypes,
      dirtyFields: dirtyFields ? dirtyFields[eventIndex] : undefined,
    })
  }

  return handleNewEvents({
    event,
    images,
    defaultValues: defaultValues?.find((dv) => dv.id === event.id),
    person,
    slsTypes,
    dirtyFields: dirtyFields ? dirtyFields[eventIndex] : undefined,
    appName,
  })
}

/**
 * Prepares dispatch actions for a list of events.
 * @param {Object} params - The parameters object.
 * @param {Array} params.events - The list of event objects.
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {Object} params.defaultValues - The default values for the events.
 * @returns {Array} List of dispatch actions for the events.
 */
function prepareDispatchesForEvents({ events, person, images, appName, slsTypes, dirtyFields, defaultValues }) {
  if (!validateDispatchInputs(person, events)) return []
  const dispatches = []

  events.forEach((event, index) => {
    const eventDispatches = getEventDispatchesForSingleEvent({
      event,
      images,
      person,
      appName,
      slsTypes,
      dirtyFields,
      defaultValues,
      eventIndex: index,
    })

    dispatches.push(...eventDispatches)
  })

  return dispatches
}

function getAddLinkDispatch(event, person) {
  if (!person) {
    return null
  }
  return {
    type: 'ADD_SUB',
    value: {
      superPartitionKey: person.partitionKey,
      subPartitionKey: event.partitionKey,
      superId: person.id,
      subId: event.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Creates a REM_SUB dispatch to unlink a person from an event.
 * @param {Object} event - The event object.
 * @param {Object} person - The person to unlink.
 * @returns {Object} The REM_SUB dispatch action.
 */
function getUnlinkDispatch(event, person) {
  if (!person) {
    return null
  }
  return {
    type: 'REM_SUB',
    value: {
      superPartitionKey: person.partitionKey,
      subPartitionKey: event.partitionKey,
      superId: person.id,
      subId: event.id,
      elementType: 'PERSON',
    },
  }
}

/**
 * Processes all events to generate link/unlink dispatches and collect record IDs.
 * Iterates through current and default events to determine which persons were added or removed.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.currentEvents - Current form events { primary: [...], additional: [...] }.
 * @param {Object} params.defaultValues - Default/original events { primary: [...], additional: [...] }.
 * @param {Array} params.images - The list of images from SLS state.
 * @returns {Object} { dispatches: Array, recordIds: Array } - Link/unlink dispatches and record IDs to track.
 */
function processEventLinkingDispatches({ currentEvents, defaultValues, images }) {
  const dispatches = []
  const recordIds = new Set()
  const eventSections = ['primary', 'additional']

  eventSections.forEach((section) => {
    const events = currentEvents?.[section] || []
    const defaultEventsList = defaultValues?.[section] || []

    events.forEach((event) => {
      // Find the corresponding default event by ID
      const defaultEvent = defaultEventsList.find((dv) => dv.id === event.id)

      if (defaultEvent) {
        // Compare linked persons between current and default
        const { addedPersons, removedPersons } = compareLinkedPersons(
          defaultEvent.linkedPersonIds,
          event.linkedPersonIds
        )

        // Generate unlink dispatches
        removedPersons?.forEach((removedPerson) => {
          dispatches.push(getUnlinkDispatch(event, removedPerson))
        })

        // Generate link dispatches and collect record IDs
        addedPersons?.forEach((addedPerson) => {
          dispatches.push(getAddLinkDispatch(event, addedPerson))

          const slsPerson = getNestedOrFloatingElementById(images, addedPerson.id)
          const recordId = slsPerson?.recordId

          // Collect record ID for SET_RECORD_PERSON_IDS dispatch
          if (recordId) {
            recordIds.add(recordId)
          }
        })
      }
    })
  })

  return {
    dispatches,
    recordIds: Array.from(recordIds),
  }
}

/**
 * Gets all dispatch actions for current events, including creates, updates, and deletes.
 * @param {Object} params - The parameters object.
 * @param {Object} params.currentEvents - The current events, grouped by type.
 * @param {Object} params.eventsById - Map of event IDs to event data.
 * @param {String} params.primaryEventId - Id of primary event of record whether attached to this person or not
 * @param {Object} params.defaultValues - The default values for the events.
 * @param {Array} params.images = The list of images from SLS state.
 * @param {Object} params.person - The person object.
 * @param {string} params.appName - The application name.
 * @param {Array} params.slsTypes - The list of SLS types.
 * @param {Object} params.dirtyFields - The dirty fields object.
 * @param {Array} params.highlightDispatches - Dispatches for highlight operations.
 * @returns {Array} List of all dispatch actions for the events.
 */
export default function getEventsDispatches({
  currentEvents,
  primaryEventId,
  defaultValues,
  images,
  person,
  appName,
  slsTypes,
  dirtyFields,
  highlightDispatches,
}) {
  const dispatches = []
  const eventSections = ['primary', 'additional']

  eventSections.forEach((section) => {
    const events = currentEvents?.[section]

    if (Array.isArray(events) && events.length > 0) {
      const eventSectionDispatches = prepareDispatchesForEvents({
        events,
        images,
        person,
        appName,
        slsTypes,
        dirtyFields: dirtyFields?.[section],
        defaultValues: defaultValues?.[section],
      })

      dispatches.push(...eventSectionDispatches)
    }
  })

  const setRecordPersonIdsDispatches = []
  if (person.recordId && dispatches.some((disp) => disp.type === 'CREATE_ELEMENT' || disp.type === 'ADD_SUB')) {
    // Catch all to set record and person ids if a change was made affecting record/person ids. This needs to happen after all create or add sub dispatches.
    setRecordPersonIdsDispatches.push({
      type: 'SET_RECORD_PERSON_IDS',
      value: person.recordId,
    })
  }

  const currentEventIds = new Set([
    ...(currentEvents?.primary?.map((e) => e.id) || []),
    ...(currentEvents?.additional?.map((e) => e.id) || []),
  ])

  const originalEventIds = new Set([
    ...(defaultValues?.primary?.map((e) => e.id) || []),
    ...(defaultValues?.additional?.map((e) => e.id) || []),
  ])

  originalEventIds.forEach((eventId) => {
    if (!currentEventIds.has(eventId)) {
      const slsEvent = getNestedOrFloatingElementById(images, eventId)
      if (!slsEvent) return

      dispatches.push(...handleDeletedEvents(slsEvent))
    }
  })

  if (!currentEventIds.has(primaryEventId) && !originalEventIds.has(primaryEventId)) {
    const deletedEvent = getNestedOrFloatingElementById(images, primaryEventId)
    if (deletedEvent) {
      dispatches.push(...handleDeletedEvents(deletedEvent))
    }
  }

  // Process link/unlink operations for all events
  const linkingResult = processEventLinkingDispatches({
    currentEvents,
    defaultValues,
    images,
  })

  dispatches.push(...linkingResult.dispatches)

  // Add record IDs from linked persons to setRecordPersonIdsDispatches
  linkingResult.recordIds.forEach((recordId) => {
    if (!setRecordPersonIdsDispatches.some((disp) => disp.value === recordId)) {
      setRecordPersonIdsDispatches.push({
        type: 'SET_RECORD_PERSON_IDS',
        value: recordId,
      })
    }
  })

  dispatches.push(...highlightDispatches)
  dispatches.push(...setRecordPersonIdsDispatches)

  return dispatches
}
